/**
Copyright (C) 2022 by SeQuenX  - All Rights Reserved
This file is part of the ComplyVantage product development,
and is released under the "Commercial License Agreement".
You should have received a copy of the Commercial License Agreement license with
this file. If not, please write to: legal@sequenx.com, or visit www.sequenx.com
*/
import React from "react";
import { hiring } from "../assets/svg";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

const JoinCareersDetails = () => {
  const { t } = useTranslation();
  const reasonsDetails = t("careers.whyUs.reasons", {
    returnObjects: true,
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24">
      {/* Page Title */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-800 mb-4">
          {t("careers.title")}
        </h1>
        <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
      </div>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, #000 1px, transparent 0)`,
            backgroundSize: "40px 40px",
          }}
        ></div>
      </div>

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-12 relative">
        <div className="flex flex-col md:flex-row items-center gap-12">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full md:w-1/2"
          >
            <img
              src={hiring}
              alt="Who_We_Logo"
              className="w-full max-w-lg mx-auto transform hover:scale-105 transition-transform duration-300"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-full md:w-1/2"
          >
            <h1 className="text-4xl font-bold text-gray-800 mb-6">
              {t("careers.joinSeQuenX.title")}
            </h1>
            <div className="space-y-4 text-lg text-gray-700">
              <p>{t("careers.joinSeQuenX.description")}</p>
              <p>{t("careers.joinSeQuenX.additionalDescription")}</p>
            </div>
            <motion.a
              href="#jobOpenings"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block mt-8 text-lg text-white bg-blue-600 px-8 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-300 shadow-lg hover:shadow-xl"
            >
              {t("careers.joinSeQuenX.ctaText")}
            </motion.a>
          </motion.div>
        </div>
      </div>

      {/* Why Join Us Section */}
      <div className="container mx-auto px-4 py-16 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            {t("careers.whyUs.title")}
          </h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-700 max-w-2xl mx-auto">
            {t("careers.whyUs.moreReasonsTitle")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {reasonsDetails.map((reason, index) => {
            const colors = [
              "from-slate-700 to-slate-800",
              "from-slate-600 to-slate-700",
              "from-slate-800 to-slate-900",
              "from-slate-700 to-slate-800",
            ];

            const icons = ["🌟", "🚀", "💡", "🎯", "🤝", "📈"];

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className={`bg-gradient-to-br ${
                  colors[index % colors.length]
                } rounded-lg p-6 shadow-lg hover:shadow-xl transition-all duration-300`}
              >
                <div className="text-3xl mb-3">
                  {icons[index % icons.length]}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {reason.title}
                </h3>
                <p className="text-gray-200">{reason.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default JoinCareersDetails;
